import React, { useState, useEffect, useRef, useMemo } from 'react';
import { CATEGORIES } from '../../constants/categories';
import { EVENT_TYPES } from '../../constants/eventTypes';
import { getCityFromZip } from '../../constants/zipData';
import { distanceMiles, isKnownZip } from '../../utils/distance';
import { fmtDate, fmtTime, isUrgent } from '../../utils/formatting';
import VendorApplyModal from '../modals/VendorApplyModal';

function OpportunitiesPage({ opps, acceptedBookings, authUser, vendorProfile, allVendorProfiles, setVendorProfile, setShowAuthModal, messageEventHost, highlightOppId, setHighlightOppId }) {
  const highlightRef = useRef(null);
  useEffect(() => {
    if (highlightOppId && highlightRef.current) {
      highlightRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      highlightRef.current.style.boxShadow = '0 0 0 3px #e8c97a';
      const timer = setTimeout(() => { if (highlightRef.current) highlightRef.current.style.boxShadow = ''; if (setHighlightOppId) setHighlightOppId(null); }, 2000);
      return () => clearTimeout(timer);
    }
  }, [highlightOppId, setHighlightOppId]);
  const isVendor = !!vendorProfile;
  const canSeeFullDetails = authUser && isVendor;
  const [filterType, setFilterType] = useState("");
  const [filterCat, setFilterCat] = useState("");
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");
  const [myZip, setMyZip] = useState(vendorProfile?.home_zip || "");
  const [myRadius, setMyRadius] = useState(0);
  const [saved, setSavedRaw] = useState(() => { try { return JSON.parse(localStorage.getItem('sjvm_saved_events')||'[]'); } catch { return []; } });
  const setSaved = (updater) => setSavedRaw(prev => { const next = typeof updater === 'function' ? updater(prev) : updater; try { localStorage.setItem('sjvm_saved_events', JSON.stringify(next)); } catch {} return next; });
  const [applyOpp, setApplyOpp] = useState(null);
  const acceptedByEvent = useMemo(() => {
    const grouped = {};
    (acceptedBookings || []).forEach(r => {
      const key = `${r.event_name}|||${r.event_date}`;
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push({ name: r.vendor_name, category: r.vendor_category });
    });
    return grouped;
  }, [acceptedBookings]);
  const [showSection, setShowSection] = useState('open');
  const [showMatching, setShowMatching] = useState(!!vendorProfile);
  const zipOk = myZip.length===5 && isKnownZip(myZip);

  const todayStr = new Date().toISOString().split('T')[0];
  const vendorCats = vendorProfile ? [...(vendorProfile.subcategories||[]), vendorProfile.category, ...(vendorProfile.metadata?.serviceCategories||[]), ...(vendorProfile.metadata?.allCategories||[])].filter(Boolean) : [];
  const future = opps
    .filter(o => o.date >= todayStr)
    .filter(o => o.vendorDiscovery !== 'closed' && o.vendorDiscovery !== 'none')
    .filter(o => !filterType || (o.eventTypes || [o.eventType]).includes(filterType))
    .filter(o => !filterCat  || o.categoriesNeeded.includes(filterCat))
    .filter(o => !filterDateFrom || o.date >= filterDateFrom)
    .filter(o => !filterDateTo || o.date <= filterDateTo)
    .filter(o => !showMatching || vendorCats.length === 0 || o.categoriesNeeded.length === 0 || o.categoriesNeeded.some(c => vendorCats.includes(c)))
    .map(o => {
      const dist = zipOk ? distanceMiles(myZip, o.zip) : null;
      return {...o, dist};
    })
    .filter(o => !myRadius || !zipOk || o.dist === null || o.dist <= myRadius)
    .sort((a,b) => {
      if (a.dist!==null && b.dist!==null) return a.dist - b.dist;
      return 0;
    });
  const openOpps = future.filter(o => !o.deadline || o.deadline >= todayStr);
  const closedOpps = future.filter(o => o.deadline && o.deadline < todayStr);
  const list = showSection === 'open' ? openOpps : closedOpps;

  return (
    <>
      <div style={{ background:"linear-gradient(135deg,#1a1410,#2d2118)", padding:"48px 40px", textAlign:"center", position:"relative", overflow:"hidden" }}>
        <div style={{ position:"absolute", inset:0, background:"radial-gradient(ellipse 60% 80% at 50% 50%,rgba(232,201,122,.08),transparent)" }} />
        <h2 style={{ fontFamily:"Playfair Display,serif", fontSize:36, color:"#fff", marginBottom:8, position:"relative" }}>
          Vendor <em style={{ color:"#e8c97a", fontStyle:"italic" }}>Opportunities</em>
        </h2>
        <p style={{ color:"#a89a8a", fontSize:16, maxWidth:520, margin:"0 auto", position:"relative" }}>
          Events, pop-ups, and markets across South Jersey actively looking for vendors.
        </p>
      </div>
      <div className="section" style={{ maxWidth:1100, paddingTop:40 }}>
        <div className="match-filters">
          <div className="match-filter-group" style={{ maxWidth:200 }}>
            <label>My Zip Code</label>
            <input placeholder="e.g. 08003" value={myZip} maxLength={5}
              onChange={e => setMyZip(e.target.value.replace(/\D/g,"").slice(0,5))} />
            {myZip.length===5 && (
              <div className={`zip-feedback ${zipOk?"zip-ok":"zip-warn"}`}>
                {zipOk ? (myRadius ? `\u2713 Within ${myRadius} miles` : "\u2713 Sorted by distance") : "\u26a0 Zip unverified"}
              </div>
            )}
          </div>
          {zipOk && (
            <div className="match-filter-group" style={{maxWidth:160}}>
              <label>Radius</label>
              <select value={myRadius} onChange={e=>setMyRadius(+e.target.value)}>
                <option value={0}>Any distance</option>
                <option value={5}>5 miles</option>
                <option value={10}>10 miles</option>
                <option value={15}>15 miles</option>
                <option value={20}>20 miles</option>
                <option value={30}>30 miles</option>
                <option value={50}>50 miles</option>
              </select>
            </div>
          )}
          <div className="match-filter-group">
            <label>Event Type</label>
            <select value={filterType} onChange={e=>setFilterType(e.target.value)}>
              <option value="">All Types</option>
              {EVENT_TYPES.map(t=><option key={t}>{t}</option>)}
            </select>
          </div>
          <div className="match-filter-group">
            <label>From Date</label>
            <input type="date" value={filterDateFrom} min={todayStr} onChange={e=>setFilterDateFrom(e.target.value)} />
          </div>
          <div className="match-filter-group">
            <label>To Date</label>
            <input type="date" value={filterDateTo} min={filterDateFrom||todayStr} onChange={e=>setFilterDateTo(e.target.value)} />
          </div>
          <div className="match-filter-group">
            <label>Category Needed</label>
            <select value={filterCat} onChange={e=>setFilterCat(e.target.value)}>
              <option value="">All Categories</option>
              {CATEGORIES.map(c=><option key={c}>{c}</option>)}
            </select>
          </div>
        </div>
        {vendorProfile && (
          <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:12,flexWrap:'wrap'}}>
            {allVendorProfiles && allVendorProfiles.length > 1 && (
              <div style={{display:'flex',gap:4,marginRight:8}}>
                {allVendorProfiles.map(p=>(
                  <button key={p.id} onClick={()=>setVendorProfile(p)} style={{padding:'4px 10px',borderRadius:6,fontSize:11,fontWeight:600,cursor:'pointer',fontFamily:'DM Sans,sans-serif',border:vendorProfile?.id===p.id?'2px solid #c8a850':'1px solid #e8ddd0',background:vendorProfile?.id===p.id?'#fdf9f0':'#fff',color:'#1a1410'}}>{p.name||p.contact_name}</button>
                ))}
              </div>
            )}
            <button onClick={()=>setShowMatching(!showMatching)} style={{
              background:showMatching?'#c8a850':'#1a1410', color:showMatching?'#1a1410':'#e8c97a',
              border:'none', borderRadius:8, padding:'8px 18px',
              fontSize:13, fontWeight:700, cursor:'pointer', fontFamily:'DM Sans,sans-serif'
            }}>{showMatching ? 'Showing Matched Events — Tap for All' : '✓ Showing All Events'}</button>
          </div>
        )}
        <div style={{ display:'flex', gap:0, marginBottom:20, borderRadius:8, overflow:'hidden', border:'1px solid #e8ddd0' }}>
          <button onClick={()=>setShowSection('open')} style={{
            flex:1, padding:'10px 16px', fontSize:13, fontWeight:700, cursor:'pointer', border:'none',
            fontFamily:'DM Sans,sans-serif',
            background: showSection==='open' ? '#1a1410' : '#fff',
            color: showSection==='open' ? '#e8c97a' : '#7a6a5a',
          }}>Open ({openOpps.length})</button>
          <button onClick={()=>setShowSection('closed')} style={{
            flex:1, padding:'10px 16px', fontSize:13, fontWeight:700, cursor:'pointer', border:'none',
            fontFamily:'DM Sans,sans-serif', borderLeft:'1px solid #e8ddd0',
            background: showSection==='closed' ? '#1a1410' : '#fff',
            color: showSection==='closed' ? '#e8c97a' : '#7a6a5a',
          }}>Coming Soon ({closedOpps.length})</button>
        </div>
        <div className="results-header">
          <div className="results-count"><strong>{list.length}</strong> {showSection==='open' ? 'open opportunities' : 'coming soon — applications closed'}</div>
          <div style={{ fontSize:13, color:"#a89a8a" }}>Past events hidden</div>
        </div>
        {list.length===0
          ? <div className="empty-state"><div className="big">📭</div><p>No opportunities match your filters.</p></div>
          : (
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(320px,1fr))", gap:24 }}>
            {list.map(opp => {
              const isExternalSource = !!(opp.source && !['Host Submitted', 'Recurring Series'].includes(opp.source));
              return (
              <div key={opp.id} ref={opp.id === highlightOppId ? highlightRef : null} style={{ background:"#fff", border:"1px solid #e8ddd0", borderRadius:12, overflow:"hidden", transition:"all 0.2s" }}>
                {/* Photo — vendors only */}
                {canSeeFullDetails && opp.photoUrl && (
                  <div style={{ position:'relative', height:160, overflow:'hidden' }}>
                    <img src={opp.photoUrl} alt={opp.eventName} style={{ width:'100%', height:'100%', objectFit:'cover', display:'block' }} />
                    <div style={{ position:'absolute', inset:0, background:'linear-gradient(to bottom, transparent 40%, rgba(26,20,16,0.7) 100%)' }} />
                    <div style={{ position:'absolute', bottom:10, left:14, display:'inline-block', background:'#e8c97a', color:'#1a1410', fontSize:10, fontWeight:700, letterSpacing:'1.5px', textTransform:'uppercase', padding:'3px 10px', borderRadius:20 }}>{opp.source}</div>
                  </div>
                )}
                {/* Header */}
                <div style={{ background:"linear-gradient(135deg,#1a1410,#2d2118)", padding:"20px 24px" }}>
                  {canSeeFullDetails && !opp.photoUrl && <div style={{ display:"inline-block", background:"#e8c97a", color:"#1a1410", fontSize:10, fontWeight:700, letterSpacing:"1.5px", textTransform:"uppercase", padding:"3px 10px", borderRadius:20, marginBottom:10 }}>{opp.source}</div>}
                  {canSeeFullDetails && (opp.orgName || opp.contactName) && <div style={{ fontSize:13, color:'#cdb98a', marginBottom:2 }}>Hosted by {opp.orgName || opp.contactName}</div>}
                  <div style={{ fontFamily:"Playfair Display,serif", fontSize:20, color:"#fff", marginBottom:4, lineHeight:1.3 }}>{canSeeFullDetails ? opp.eventName : (opp.eventTypes || [opp.eventType]).join(' · ')}</div>
                  <div style={{ fontSize:12, color:"#a89a8a", letterSpacing:"1px", textTransform:"uppercase" }}>{(opp.eventTypes || [opp.eventType]).join(' · ')}</div>
                  {(opp.vendorStatus === 'fully_booked' || (opp.spots && (acceptedByEvent[`${opp.eventName}|||${opp.date}`] || []).length >= opp.spots)) && <span style={{display:'inline-block',marginTop:8,background:'#e8c97a',color:'#1a1410',padding:'3px 12px',borderRadius:12,fontSize:11,fontWeight:700}}>Event Full</span>}
                  {opp.vendorStatus === 'closed' && <span style={{display:'inline-block',marginTop:8,background:'#f5f0ea',color:'#7a6a5a',padding:'3px 12px',borderRadius:12,fontSize:11,fontWeight:700}}>Applications Closed</span>}
                  {(!opp.vendorStatus || opp.vendorStatus === 'open') && opp.deadline && (() => { const d = new Date(opp.deadline); const now = new Date(); const hrs = (d - now)/(1000*60*60); return hrs > 0 && hrs <= 48 ? <span style={{display:'inline-block',marginTop:8,background:'#fdecea',color:'#8b1a1a',padding:'3px 12px',borderRadius:12,fontSize:11,fontWeight:700}}>Closing Soon</span> : null; })()}
                </div>
                <div style={{ padding:"20px 24px" }}>
                  {/* Preview fields — visible to everyone */}
                  {(opp.vendorNotes || opp.notes) && <p style={{ fontSize:13, color:"#7a6a5a", lineHeight:1.6, marginBottom:14, padding:12, background:"#fdf9f5", borderRadius:6, borderLeft:"3px solid #e8c97a" }}>{opp.vendorNotes || opp.notes}</p>}
                  <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'10px 20px',marginBottom:14}}>
                    <div><div style={{ fontSize:10, textTransform:"uppercase", letterSpacing:1, color:"#a89a8a", fontWeight:600 }}>Date</div><div style={{ fontSize:14, fontWeight:500 }}>{fmtDate(opp.date)}</div></div>
                    <div><div style={{ fontSize:10, textTransform:"uppercase", letterSpacing:1, color:"#a89a8a", fontWeight:600 }}>Time</div><div style={{ fontSize:14, fontWeight:500 }}>{fmtTime(opp.startTime)}{opp.endTime ? ' – '+fmtTime(opp.endTime) : ''}</div></div>
                    <div><div style={{ fontSize:10, textTransform:"uppercase", letterSpacing:1, color:"#a89a8a", fontWeight:600 }}>Event Fee</div><div style={{ fontSize:14, fontWeight:500 }}>{opp.boothFee || 'Not specified'}</div></div>
                    <div><div style={{ fontSize:10, textTransform:"uppercase", letterSpacing:1, color:"#a89a8a", fontWeight:600 }}>Admission</div><div style={{ fontSize:14, fontWeight:500 }}>{opp.isTicketed ? '🎟️ '+(opp.ticketPrice||'Ticketed') : 'Free'}</div></div>
                    {opp.deadline && <div><div style={{ fontSize:10, textTransform:"uppercase", letterSpacing:1, color:"#a89a8a", fontWeight:600 }}>Apply By</div><div style={{ fontSize:14, fontWeight:500, color:isUrgent(opp.deadline)?'#8b0000':'#1a1410' }}>{isUrgent(opp.deadline)?'🔥 ':''}{fmtDate(opp.deadline)}</div></div>}
                  </div>

                  {/* Vendor full details */}
                  {canSeeFullDetails ? (
                    <>
                      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'10px 20px',marginBottom:14,paddingTop:14,borderTop:'1px solid #e8ddd0'}}>
                        <div><div style={{ fontSize:10, textTransform:"uppercase", letterSpacing:1, color:"#a89a8a", fontWeight:600 }}>Host</div><div style={{ fontSize:14, fontWeight:500 }}>{opp.contactName}</div></div>
                        <div><div style={{ fontSize:10, textTransform:"uppercase", letterSpacing:1, color:"#a89a8a", fontWeight:600 }}>Location</div><div style={{ fontSize:14, fontWeight:500 }}>{getCityFromZip(opp.zip) || 'Zip '+opp.zip}{opp.dist!==null ? ` · ${opp.dist.toFixed(1)}mi away` : ""}</div></div>
                        {(() => {
                          const accepted = acceptedByEvent[`${opp.eventName}|||${opp.date}`] || [];
                          const spotsLeft = opp.spots ? Math.max(0, opp.spots - accepted.length) : null;
                          return (
                            <div><div style={{ fontSize:10, textTransform:"uppercase", letterSpacing:1, color:"#a89a8a", fontWeight:600 }}>Vendor Spots</div>
                              <div style={{ fontSize:14, fontWeight:500, color: spotsLeft === 0 ? '#8b1a1a' : '#1a1410' }}>
                                {opp.spots ? `${spotsLeft} of ${opp.spots} available` : 'Contact Host'}
                              </div>
                            </div>
                          );
                        })()}
                        <div>
                          <div style={{ fontSize:10, textTransform:"uppercase", letterSpacing:1, color:"#a89a8a", fontWeight:600 }}>Contact</div>
                          <div style={{ fontSize:13 }}>
                            {opp.contactEmail && <div><a href={`mailto:${opp.contactEmail}`} style={{ color:'#c8a84b', textDecoration:'underline' }}>{opp.contactEmail}</a></div>}
                            {opp.contactPhone && <div><a href={`tel:${opp.contactPhone}`} style={{ color:'#c8a84b', textDecoration:'underline' }}>{opp.contactPhone}</a></div>}
                            {opp.eventLink && <div><a href={opp.eventLink} target="_blank" rel="noopener noreferrer" style={{ color:'#c8a84b', textDecoration:'underline' }}>Event Link</a></div>}
                            {!opp.contactEmail && !opp.contactPhone && !opp.eventLink && <div style={{ color:'#a89a8a' }}>—</div>}
                          </div>
                        </div>
                      </div>
                      {opp.categoriesNeeded.length > 0 && (
                        <div style={{marginBottom:14}}>
                          <div style={{ fontSize:10, textTransform:"uppercase", letterSpacing:1, color:"#a89a8a", fontWeight:600, marginBottom:6 }}>Categories Needed</div>
                          <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
                            {opp.categoriesNeeded.map(c=><span key={c} style={{ background:"#f5f0ea", border:"1px solid #e8ddd0", padding:"3px 10px", borderRadius:20, fontSize:11, color:"#5a4a3a" }}>{c}</span>)}
                          </div>
                        </div>
                      )}
                      {opp.servicesNeeded && opp.servicesNeeded.length > 0 && (
                        <div style={{marginBottom:14}}>
                          <div style={{ fontSize:10, textTransform:"uppercase", letterSpacing:1, color:"#a89a8a", fontWeight:600, marginBottom:6 }}>Services Needed</div>
                          {opp.servicesNeeded.map((svc,i)=>(
                            <div key={i} style={{background:'#f5f0ea',border:'1px solid #e8ddd0',borderRadius:8,padding:'8px 12px',marginBottom:6,fontSize:13}}>
                              <strong style={{color:'#1a1410'}}>{svc.type || 'Service'}{svc.subType && svc.subType!=='Other' ? ` — ${svc.subType}` : ''}{svc.otherType ? ` — ${svc.otherType}` : ''}</strong>
                              <span style={{color:'#7a6a5a'}}> · {svc.duration}</span>
                              <span style={{color:'#7a6a5a'}}> · Budget: {svc.budgetType==='open' ? 'Open to quotes' : svc.budgetType==='fixed' ? (svc.budgetAmount||'TBD') : (svc.budgetMin||'?')+' – '+(svc.budgetMax||'?')}</span>
                              {svc.notes && <div style={{fontSize:11,color:'#a89a8a',marginTop:2}}>{svc.notes}</div>}
                            </div>
                          ))}
                        </div>
                      )}
                      {/* Accepted vendors list */}
                      {(() => {
                        const accepted = acceptedByEvent[`${opp.eventName}|||${opp.date}`] || [];
                        if (accepted.length === 0) return null;
                        return (
                          <div style={{marginBottom:14}}>
                            <div style={{ fontSize:10, textTransform:"uppercase", letterSpacing:1, color:"#a89a8a", fontWeight:600, marginBottom:6 }}>Confirmed Vendors ({accepted.length})</div>
                            <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
                              {accepted.map((v, i) => <span key={i} style={{ background:"#f5f0ea", border:"1px solid #e8ddd0", padding:"3px 10px", borderRadius:20, fontSize:11, color:"#5a4a3a" }}>{v.name}{v.category ? ` · ${v.category}` : ''}</span>)}
                            </div>
                          </div>
                        );
                      })()}
                      {(() => {
                        const accepted = acceptedByEvent[`${opp.eventName}|||${opp.date}`] || [];
                        const isFull = opp.spots && accepted.length >= opp.spots;
                        return (
                      <div style={{ display:"flex", gap:10, flexWrap:'wrap' }}>
                        {opp.vendorLink ? (
                          <a href={opp.vendorLink} target="_blank" rel="noopener noreferrer" style={{ flex:2, minWidth:140, background:"#c8a84b", color:"#1a1410", border:"none", padding:"10px 16px", borderRadius:6, fontSize:13, fontWeight:700, cursor:"pointer", textAlign:"center", textDecoration:"none", display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"DM Sans,sans-serif" }}>Apply on External Site</a>
                        ) : isExternalSource ? null : (
                          <>
                            {opp.vendorStatus === 'fully_booked' || isFull ? (
                              <div style={{ flex:2, minWidth:140, padding:"10px 16px", borderRadius:6, fontSize:13, fontWeight:700, textAlign:'center', background:'#1a1410', color:'#e8c97a' }}>Event Full</div>
                            ) : opp.vendorStatus === 'closed' ? (
                              <div style={{ flex:2, minWidth:140, padding:"10px 16px", borderRadius:6, fontSize:13, fontWeight:600, textAlign:'center', background:'#f5f0ea', color:'#a89a8a', border:'1px solid #e8ddd0' }}>Applications Closed</div>
                            ) : showSection==='closed' ? (
                              <div style={{ flex:2, minWidth:140, padding:"10px 16px", borderRadius:6, fontSize:13, fontWeight:600, textAlign:'center', background:'#f5f0ea', color:'#a89a8a', border:'1px solid #e8ddd0' }}>Past Deadline</div>
                            ) : (opp.vendorDiscovery === 'apply' || opp.vendorDiscovery === 'both') ? (
                              <button onClick={()=>setApplyOpp(opp)} style={{ flex:2, minWidth:140, background:"#c8a84b", color:"#1a1410", border:"none", padding:"10px 16px", borderRadius:6, fontSize:13, fontWeight:700, cursor:"pointer", fontFamily:"DM Sans,sans-serif" }}>
                                Apply to Vend
                              </button>
                            ) : null}
                          </>
                        )}
                        {!isExternalSource && messageEventHost && authUser && vendorProfile && (
                          <button onClick={()=>messageEventHost(opp)} style={{ flex:1, minWidth:100, background:"#1a1410", color:"#e8c97a", border:"none", padding:"10px 16px", borderRadius:6, fontSize:13, fontWeight:700, cursor:"pointer", fontFamily:"DM Sans,sans-serif" }}>
                            💬 Message Host
                          </button>
                        )}
                        {opp.fbLink && <a href={opp.fbLink} target="_blank" rel="noopener noreferrer" style={{ flex:1, minWidth:100, background:"#1a1410", color:"#e8c97a", border:"none", padding:"10px 16px", borderRadius:6, fontSize:13, fontWeight:600, cursor:"pointer", textAlign:"center", textDecoration:"none", display:"flex", alignItems:"center", justifyContent:"center" }}>View Event Details</a>}
                        <button onClick={()=>setSaved(s=>s.includes(opp.id)?s.filter(x=>x!==opp.id):[...s,opp.id])} style={{ background:saved.includes(opp.id)?"#fdf4dc":"#f5f0ea", color:"#1a1410", border:"1px solid #e0d5c5", padding:"10px 16px", borderRadius:6, fontSize:13, fontWeight:600, cursor:"pointer", fontFamily:"DM Sans,sans-serif" }}>
                          {saved.includes(opp.id)?"\u2713 Saved":"Save"}
                        </button>
                      </div>
                        );
                      })()}
                    </>
                  ) : (
                    /* Guest / non-vendor CTA */
                    <div style={{background:'#fdf9f5',border:'1px solid #e8ddd0',borderRadius:8,padding:'16px 20px',textAlign:'center'}}>
                      <div style={{fontSize:13,color:'#7a5a10',marginBottom:10,lineHeight:1.5}}>🔒 Sign up as a vendor to view full event details and apply</div>
                      <button onClick={()=>{if(setShowAuthModal)setShowAuthModal(true);}}
                        style={{background:'#c8a84b',color:'#1a1410',border:'none',borderRadius:6,padding:'10px 28px',fontSize:14,fontWeight:700,cursor:'pointer',fontFamily:'DM Sans,sans-serif'}}>
                        Sign Up as a Vendor
                      </button>
                    </div>
                  )}
                </div>
              </div>
              );
            })}
          </div>
        )}
      </div>
      {applyOpp && <VendorApplyModal opp={applyOpp} allOpps={opps} onClose={()=>setApplyOpp(null)} />}
    </>
  );
}

export default OpportunitiesPage;
